import type { ProductCategoryId } from './constants'
import type { FactoryTaxonomyConfidence, FactoryTaxonomyKey } from './schemas/factories'
import { resolveCategoryId, TAXONOMY, type Axis } from './taxonomy'

/**
 * 공장 분류 자동 추정 (대표 결정 10/30: 각 식품·메뉴에 따라 분류). 공장이 적은 품목·업종 글자에서 레시피캣 taxonomy 의
 * 대·중분류를 규칙으로 고른다. 관리자가 고치면 confidence 가 manual 이 되고 다시 돌리지 않는다.
 *
 * 점수: 대분류 별칭(아래 표) 1점, 식품 축 세부 이름 낱말 2점. 식품 축 대분류 중 점수가 가장 높은 것과 그 절반 이상인 것(최대 3개)을
 * 고르고, 메뉴 축은 걸린 것만 더한다. 애매하면(점수 1, 동점 셋 이상, 식품 축 0) '분류 확인 필요'.
 */

/**
 * 대분류 별칭 — 영업 리스트 카테고리와 공장들이 쓰는 말. 글자 포함으로 찾으므로 한 글자 낱말과 다른 말 속에 숨는 낱말은 넣지 않는다
 * (예: '커리'는 '베이커리', '떡'은 '떡볶이 소스', '발효'는 '발효유'에 걸린다)
 */
const MAJOR_ALIASES: Record<Axis, Record<string, string[]>> = {
  product: {
    sauce: ['소스', '양념', '시즈닝', '드레싱', '장류', '고추장', '간장', '된장', '조미'],
    'meal-kit': ['밀키트'],
    beverage: ['음료', '에이드', '액상차', '파우치음료', '젤리스틱'],
    coffee: ['커피', '원두', '콜드브루', '로스팅'],
    alcohol: ['주류', '막걸리', '양조', '증류', '와인', '맥주', '전통주'],
    misc: ['커스텀'],
    frozen: ['냉동식품', '냉동'],
    hmr: ['간편식', 'HMR', '도시락', '레토르트', '즉석'],
    snacks: ['스낵', '과자', '쌀과자', '그래놀라', '강정'],
    bakery: ['베이커리', '제과', '제빵', '쿠키', '케이크'],
    dairy: ['유제품', '우유', '요거트', '요구르트', '치즈', '발효유'],
    meat: ['육가공', '축산', '소시지', '육포', '돈까스', '돈가스', '갈비'],
    seafood: ['수산', '어묵', '젓갈', '해산물', '수산가공'],
    supplement: ['건강기능', '건기식', '정제형', '캡슐', '홍삼', '스틱충전'],
    pickled: ['절임', '장아찌', '김치', '피클', '발효식품'],
    noodles: ['면류', '국수', '라면', '냉면', '칼국수', '우동'],
    canned: ['통조림', '병조림'],
    dried: ['건조', '동결건조', '말린'],
    baby: ['이유식', '유아식', '영유아'],
    sports: ['단백질', '프로틴', '다이어트', '체중조절'],
    pet: ['펫푸드', '반려동물', '반려견'],
    oils: ['유지', '참기름', '들기름', '식용유', '오일', '라드'],
    grains: ['곡물', '곡류', '견과', '두류', '잡곡', '누룽지', '선식', '미숫가루'],
    produce: ['과채', '과일', '채소', '과일가공', '채소가공', '퓨레'],
    tea: ['허브', '티백', '침출차'],
    'plant-based': ['비건', '식물성', '대체육'],
    tteok: ['떡류', '떡집', '가래떡', '인절미', '송편', '한과', '약과'],
    // 2026-11-01 장류 대분류. 소스 별칭에도 장 낱말이 있어 장 공장은 두 대분류에 같이 걸린다
    jang: ['장류', '고추장', '된장', '간장', '청국장', '메주', '춘장'],
  },
  menu: {
    main: ['직화', '스테이크', '찜닭'],
    'rice-noodle': ['덮밥', '김밥', '비빔밥', '볶음밥'],
    intl: ['파스타', '피자', '카레'],
    soup: ['찌개', '곰탕', '국밥', '육수'],
    side: ['반찬', '샐러드'],
    anju: ['안주'],
    dessert: ['디저트', '젤라또', '아이스크림', '푸딩', '빙수'],
    drinks: ['스무디', '음료 메뉴'],
  },
  // 코스·테이스팅은 식당 코스의 자리라 공장 품목으로 고르지 않는다 (공장 분류는 식품·메뉴 축만)
  course: {},
}

/** 세부 이름에서 낱말을 뽑을 때 버리는 말 (어디에나 나와 분류를 가르지 못한다) */
const STOP = new Set([
  '기타',
  '일반',
  '가공',
  '제품',
  '식품',
  '요리',
  '소포장',
  '단일',
  '가루',
  '블렌드',
  // 2026-11-01 식품 명세 이름에서 생기거나 늘어난 말. '주식'은 '주식회사'에 숨고, '고기'·'우유'·'과일'은 여러 대분류
  // 공장 품목에 흔하고, '가공식품'·'가공품'·'포함'은 어디에나 나온다 (대분류 별칭은 이 표와 상관없이 그대로 센다)
  '주식',
  '포함',
  '고기',
  '우유',
  '과일',
  '가공식품',
  '가공품',
])

const words = (label: string) =>
  label
    .split(/[\s·/(),~+&-]+/)
    .map((w) => w.trim())
    .filter((w) => w.length >= 2 && !STOP.has(w))

/**
 * 세부 낱말 표 (모듈을 처음 읽을 때 한 번 만든다). 식품 축만 쓴다 — 메뉴 축 세부 이름(음료·디저트·차)은 공장 품목 글자에
 * 너무 흔해 메뉴 축은 별칭으로만 고른다. 고르기 화면에서 숨긴 중복 세부(기타의 분말·스낵 등)도 뺀다
 */
const SUB_WORDS = TAXONOMY.product.flatMap((major) =>
  major.subs
    .filter((sub) => !sub.hiddenInPicker)
    .map((sub) => ({
      axis: 'product' as const,
      majorId: major.id,
      subId: sub.id,
      words: words(sub.ko),
    })),
)

export interface FactoryTaxonomyGuess {
  keys: FactoryTaxonomyKey[]
  confidence: Exclude<FactoryTaxonomyConfidence, 'manual'> | null
  needsReview: boolean
  /** 식품 축 키에서 나온 파이프라인 분류 (견적 고르기 순서에 쓰는 factories.categoryIds) */
  categoryIds: ProductCategoryId[]
}

/** 품목·업종 글자 → 분류 추정. 같은 글자면 늘 같은 결과 */
export function classifyFactoryTaxonomy(text: string): FactoryTaxonomyGuess {
  const hay = text.normalize('NFKC').toLowerCase()
  const has = (w: string) => hay.includes(w.toLowerCase())
  const score = new Map<string, number>()
  const subsHit = new Map<string, string[]>()
  const add = (key: string, n: number) => score.set(key, (score.get(key) ?? 0) + n)
  for (const axis of ['product', 'menu'] as const)
    for (const [majorId, aliases] of Object.entries(MAJOR_ALIASES[axis]))
      for (const alias of aliases) if (has(alias)) add(`${axis}:${majorId}`, 1)
  for (const sub of SUB_WORDS) {
    if (!sub.words.some(has)) continue
    const key = `${sub.axis}:${sub.majorId}`
    add(key, 2)
    subsHit.set(key, [...(subsHit.get(key) ?? []), sub.subId])
  }

  const ranked = (axis: Axis) =>
    [...score.entries()]
      .filter(([k]) => k.startsWith(`${axis}:`))
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
  const product = ranked('product')
  const top = product[0]?.[1] ?? 0
  const chosen = product.filter(([, n]) => n >= top / 2).slice(0, 3)
  const menu = ranked('menu').slice(0, 2)

  const toKeys = (entries: [string, number][]): FactoryTaxonomyKey[] =>
    entries.flatMap(([key]) => {
      const [axis, majorId] = key.split(':') as [Axis, string]
      const subs = subsHit.get(key) ?? []
      // 세부가 하나만 걸렸으면 세부까지, 여럿이거나 없으면 대분류만 (세부 추정이 갈리면 틀리기 쉽다)
      return [{ axis, majorId, subId: subs.length === 1 ? subs[0]! : null }]
    })
  const keys = [...toKeys(chosen), ...toKeys(menu)]
  const tiedTop = product.filter(([, n]) => n === top).length
  const confidence =
    top === 0 ? null : top >= 3 && tiedTop === 1 ? 'high' : top >= 2 ? 'medium' : 'low'
  const categoryIds = [
    ...new Set(
      keys
        .filter((k) => k.axis === 'product')
        .map((k) => resolveCategoryId(k.axis, k.majorId, k.subId))
        .filter((c): c is ProductCategoryId => c !== null),
    ),
  ]
  return {
    keys,
    confidence,
    needsReview: top === 0 || confidence === 'low' || tiedTop >= 3,
    categoryIds,
  }
}

export interface FactoryMajorCount {
  axis: Axis
  majorId: string
  count: number
}

/**
 * 분류별 공장 수 (관리자 공장 목록 요약·시드 도구 미리보기). 공장 하나는 대분류마다 한 번 센다. 많은 순, 같으면 taxonomy 순서
 */
export function countFactoriesByMajor(
  factories: readonly { taxonomyKeys: readonly FactoryTaxonomyKey[] }[],
): FactoryMajorCount[] {
  const counts = new Map<string, number>()
  for (const f of factories)
    for (const key of new Set(f.taxonomyKeys.map((k) => `${k.axis}:${k.majorId}`)))
      counts.set(key, (counts.get(key) ?? 0) + 1)
  const order = (['product', 'menu'] as const).flatMap((axis) =>
    TAXONOMY[axis].map((m) => `${axis}:${m.id}`),
  )
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || order.indexOf(a[0]) - order.indexOf(b[0]))
    .map(([key, count]) => {
      const [axis, majorId] = key.split(':') as [Axis, string]
      return { axis, majorId, count }
    })
}
